import { useCallback, useEffect, useState } from 'react';

import { api } from '../api';

// Mirrors the server's ClientConfig payload (src/api/config/routes.ts).
export interface ClientConfig {
  turnstileSiteKey: string | null;
  /** MapTiler publishable key for the dashboard map + weather layers;
   * null → the map module renders its honest "map unavailable" state. */
  maptilerApiKey: string | null;
  /** Whether Neo has an LLM configured — gates Neo CTAs. */
  neoEnabled: boolean;
  // Latest published core version from the verified release manifest
  // (core-update-mechanism). Null = manifest unconfigured / not yet verified.
  coreLatestVersion: string | null;
  /** Whether Try it is on (TOM-867, TOM-868). Absent from an older server: off. */
  tryIt?: boolean;
  /** PostHog project key (TOM-983); null or absent → analytics stay off. */
  posthogKey?: string | null;
  /** PostHog ingestion origin; absent from an older server. */
  posthogHost?: string;
}

export interface ClientConfigState {
  config: ClientConfig | null;
  // Settled — the fetch resolved or rejected. Lets a caller block actions
  // until it knows whether Turnstile is required, rather than acting on the
  // null-during-load value.
  loaded: boolean;
  // The fetch failed. config is null and must NOT be trusted as "Turnstile
  // not configured": when the server enforces Turnstile, a tokenless submit
  // is guaranteed to be rejected, so the caller should surface the error and
  // offer a retry rather than silently dropping the widget.
  error: boolean;
  reload: () => void;
}

// One in-flight/resolved fetch shared across every useClientConfig() caller.
// The payload is a small, per-deploy-constant document, but the dashboard alone
// mounts three consumers (Dashboard, OverviewPane, AnchorWatchModule) that each
// used to fire their own GET /v1/config — and the anchor map can't mount until
// its consumer's copy resolves. Sharing one promise collapses those to a single
// request and means the config is usually already resolved by the time the map
// needs the MapTiler key. A rejection is NEVER cached: a transient failure must
// stay retryable via reload() (Signup / ForgotPassword) — see the e2e that
// aborts the first /v1/config then expects the retry to succeed.
let configPromise: Promise<ClientConfig> | null = null;

function loadClientConfig(): Promise<ClientConfig> {
  if (configPromise === null) {
    configPromise = api<ClientConfig>('/v1/config').catch((err) => {
      configPromise = null;
      throw err;
    });
  }
  return configPromise;
}

// Fetches the public runtime config (GET /v1/config). The signup form uses
// it to learn the Turnstile site key, which can't be baked into the single
// shared SPA build (ADR 0030).
export function useClientConfig(): ClientConfigState {
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    setError(false);
    loadClientConfig()
      .then((c) => {
        if (!active) return;
        setConfig(c);
        setError(false);
      })
      .catch(() => {
        if (!active) return;
        // Do NOT fail open to a null site key. Treating a fetch failure as
        // "no Turnstile" sends a tokenless request that the server rejects
        // whenever a secret is set — turning a transient blip into a hard
        // signup failure. Surface it so the form blocks submit and retries.
        setConfig(null);
        setError(true);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [nonce]);

  // Bust the shared cache first so the bumped nonce triggers a real refetch
  // (a successful config is otherwise cached for the life of the page) — the
  // retry path after a transient /v1/config failure.
  const reload = useCallback(() => {
    configPromise = null;
    setNonce((n) => n + 1);
  }, []);

  return { config, loaded, error, reload };
}
